import { useEffect, useState } from 'react'
import { Button, Spinner } from '@w7/ui'
import { getPlayerBaseUrl } from '@/api/client'
import {
  clearCachesAndReload,
  fetchClientConfig,
  fetchDeployedVersion,
  getAppBuildId,
  needsForcedReload,
  webDeployNeedsReload,
} from './clientConfig'
import './version-gate.css'

type GateState = 'checking' | 'ready' | 'update-required' | 'reloading'

const LOCK_KEY = 'strix_backend_version_gate_lock_v1'

function readLock(): boolean {
  try {
    return sessionStorage.getItem(LOCK_KEY) === '1'
  } catch {
    return false
  }
}

function writeLock(on: boolean) {
  try {
    if (on) sessionStorage.setItem(LOCK_KEY, '1')
    else sessionStorage.removeItem(LOCK_KEY)
  } catch {
    // ignore
  }
}

/**
 * Runs before the rest of the console. When force-reload is required, shows a
 * non-dismissible update alert — no console operations until Update now.
 */
export function VersionGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GateState>(() =>
    readLock() ? 'update-required' : 'checking',
  )
  const [busy, setBusy] = useState(false)
  const currentBuildId = getAppBuildId()
  const [requiredBuildId, setRequiredBuildId] = useState('')

  useEffect(() => {
    let cancelled = false

    async function run() {
      // Every image deploy: same-origin stamp (no Player-API push required).
      const deployed = await fetchDeployedVersion()
      if (cancelled) return
      if (
        deployed &&
        webDeployNeedsReload(getAppBuildId(), deployed.buildId)
      ) {
        writeLock(false)
        setRequiredBuildId(deployed.buildId)
        setState('reloading')
        await clearCachesAndReload()
        return
      }

      const base = getPlayerBaseUrl()
      if (!base) {
        if (!cancelled) {
          writeLock(false)
          setState('ready')
        }
        return
      }

      const config = await fetchClientConfig(base)
      if (cancelled) return

      if (needsForcedReload(config?.backendUi)) {
        writeLock(true)
        setRequiredBuildId((config?.backendUi.minBuildId || '').trim())
        setState('update-required')
        return
      }

      if (config == null && readLock()) {
        setState('update-required')
        return
      }

      writeLock(false)
      setState('ready')
    }

    void run()

    const onFocus = () => {
      void run()
    }
    const interval = window.setInterval(() => {
      void run()
    }, 45_000)

    window.addEventListener('focus', onFocus)
    return () => {
      cancelled = true
      window.removeEventListener('focus', onFocus)
      window.clearInterval(interval)
    }
  }, [])

  const onUpdate = async () => {
    if (busy) return
    setBusy(true)
    setState('reloading')
    try {
      await clearCachesAndReload()
    } finally {
      setBusy(false)
    }
  }

  if (state === 'ready') return <>{children}</>

  if (state === 'checking') {
    return (
      <div className="w7-auth w7-auth-center" data-testid="version-checking">
        <Spinner size={22} />
        <p className="w7-dim" style={{ marginTop: 12 }}>
          Checking for updates…
        </p>
      </div>
    )
  }

  return (
    <div className="w7-version-gate" data-testid="version-update-required">
      <div className="w7-version-gate-card">
        <p className="w7-version-gate-brand">Strix</p>
        <h1 className="w7-version-gate-title">Update required</h1>
        <p className="w7-version-gate-body">
          A newer version of the agent console is required before you can continue.
          No operations are available until you update.
        </p>
        {(requiredBuildId || currentBuildId) && (
          <p className="w7-dim w7-version-gate-meta">
            Current build <code>{currentBuildId}</code>
            {requiredBuildId ? (
              <>
                {' '}
                · required <code>{requiredBuildId}</code>
              </>
            ) : null}
          </p>
        )}
        {state === 'reloading' ? (
          <div className="w7-version-gate-busy">
            <Spinner size={20} />
            <span>Loading new version…</span>
          </div>
        ) : (
          <Button variant="primary" busy={busy} onClick={() => void onUpdate()}>
            Update now
          </Button>
        )}
      </div>
    </div>
  )
}
